import type { TimeOffset } from '@travelity/api/generated';

type ScheduleTimeOption = {
    value: string;
    label: string;
};

const normalizeOffsetsToMinutes = (offsets: Array<TimeOffset>): number[] => {
    return offsets
        .map(t => {
            const days = t.days ?? 0;
            const hours = t.hours ?? 0;
            const minutes = t.minutes ?? 0;
            const seconds = t.seconds ?? 0;

            const total =
                days * 24 * 60 +
                hours * 60 +
                minutes +
                Math.round(seconds / 60);

            return ((total % (24 * 60)) + 24 * 60) % (24 * 60);
        })
        .filter(m => Number.isFinite(m));
};

const toHHmm = (hh: number, mm: number): string => {
    const h = String(hh).padStart(2, '0');
    const m = String(mm).padStart(2, '0');
    return `${h}:${m}`;
};

const buildAllDayOptions = (): Array<ScheduleTimeOption> => {
    const out: ScheduleTimeOption[] = [];
    for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
        const hh = Math.floor(minutes / 60);
        const mm = minutes % 60;
        const formatted = toHHmm(hh, mm);
        out.push({ value: formatted, label: formatted });
    }
    return out;
};

export const getScheduleTimeOptions = (input?: {
    any_time?: boolean;
    start_times?: Array<TimeOffset>;
}): Array<ScheduleTimeOption> => {
    if (!input) return [];
    if (input.any_time) {
        return buildAllDayOptions();
    }

    const startTimes = input.start_times ?? [];
    if (startTimes.length === 0) return [];

    const normalized = normalizeOffsetsToMinutes(startTimes);
    const uniqueSortedMinutes = Array.from(new Set(normalized)).sort(
        (a, b) => a - b
    );

    return uniqueSortedMinutes.map(totalMinutes => {
        const hh = Math.floor(totalMinutes / 60);
        const mm = totalMinutes % 60;
        const formatted = toHHmm(hh, mm);

        return { value: formatted, label: formatted };
    });
};
